import { createContext, useCallback, useMemo, useState, type ReactNode } from 'react'
import { ConfirmModal } from './ConfirmModal'

export interface ConfirmOptions {
  title: string
  message: string
  confirmLabel?: string
  cancelLabel?: string
  danger?: boolean
}

interface ConfirmContextValue {
  confirm: (options: ConfirmOptions) => Promise<boolean>
}

export const ConfirmModalContext = createContext<ConfirmContextValue | null>(null)

interface PendingConfirm {
  options: ConfirmOptions
  resolve: (result: boolean) => void
}

interface ConfirmModalProviderProps {
  children: ReactNode
}

export function ConfirmModalProvider({ children }: ConfirmModalProviderProps) {
  const [pending, setPending] = useState<PendingConfirm | null>(null)

  const confirm = useCallback(
    (options: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        setPending({ options, resolve })
      }),
    [],
  )

  const handleResolve = useCallback(
    (result: boolean) => {
      if (pending) {
        pending.resolve(result)
        setPending(null)
      }
    },
    [pending],
  )

  const value = useMemo<ConfirmContextValue>(() => ({ confirm }), [confirm])

  return (
    <ConfirmModalContext.Provider value={value}>
      {children}
      {pending ? (
        <ConfirmModal
          title={pending.options.title}
          message={pending.options.message}
          confirmLabel={pending.options.confirmLabel ?? 'Confirm'}
          cancelLabel={pending.options.cancelLabel ?? 'Cancel'}
          danger={pending.options.danger ?? false}
          onConfirm={() => {
            handleResolve(true)
          }}
          onCancel={() => {
            handleResolve(false)
          }}
        />
      ) : null}
    </ConfirmModalContext.Provider>
  )
}
